import React from 'react';
import type { InvoiceStatus } from '@lib/api/invoices';

interface InvoiceStatusBadgeProps {
  status: InvoiceStatus;
  size?: 'sm' | 'md' | 'lg';
}

export const InvoiceStatusBadge: React.FC<InvoiceStatusBadgeProps> = ({
  status,
  size = 'md',
}) => {
  const getStatusColor = (status: InvoiceStatus) => {
    const statusMap: Record<InvoiceStatus, { bg: string; text: string; border: string }> = {
      draft: { bg: 'bg-gray-500/10', text: 'text-gray-400', border: 'border-gray-500/20' },
      sent: { bg: 'bg-blue-500/10', text: 'text-blue-400', border: 'border-blue-500/20' },
      viewed: { bg: 'bg-yellow-500/10', text: 'text-yellow-400', border: 'border-yellow-500/20' },
      partial: { bg: 'bg-orange-500/10', text: 'text-orange-400', border: 'border-orange-500/20' },
      paid: { bg: 'bg-green-500/10', text: 'text-green-400', border: 'border-green-500/20' },
      overdue: { bg: 'bg-red-500/10', text: 'text-red-400', border: 'border-red-500/20' },
      cancelled: { bg: 'bg-gray-500/10', text: 'text-gray-500 line-through', border: 'border-gray-500/20' },
      revised: { bg: 'bg-purple-500/10', text: 'text-purple-400', border: 'border-purple-500/20' },
    };

    return statusMap[status] || statusMap.draft;
  };

  const getStatusLabel = (status: InvoiceStatus) => {
    const labelMap: Record<InvoiceStatus, string> = {
      draft: 'Draft',
      sent: 'Sent',
      viewed: 'Viewed',
      partial: 'Partial',
      paid: 'Paid',
      overdue: 'Overdue',
      cancelled: 'Cancelled',
      revised: 'Revised',
    };

    return labelMap[status] || status;
  };

  const sizeClasses = {
    sm: 'px-2 py-0.5 text-xs',
    md: 'px-3 py-1 text-sm',
    lg: 'px-4 py-1.5 text-base',
  };

  const colors = getStatusColor(status);
  const label = getStatusLabel(status);

  return (
    <span
      className={`
        inline-flex items-center gap-1.5 rounded-full border font-medium
        ${colors.bg} ${colors.text} ${colors.border}
        ${sizeClasses[size]}
      `}
    >
      <span className="w-1.5 h-1.5 rounded-full bg-current"></span>
      {label}
    </span>
  );
};
